import { useHandler } from "@redotech/react-util/hook";
import { assertNever } from "@redotech/util/type";
import classnames from "classnames";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { BgColorValue } from "../../theme/box";
import { TextSizeValue } from "../../theme/typography";
import { RedoRadioButton } from "../radio/redo-radio-button";
import { RedoCheckbox, RedoCheckboxSize } from "./redo-checkbox";
import * as css from "./redo-checkbox-card.module.css";

export const checkboxCardInputTypes = ["radio", "checkbox"] as const;
export type CheckboxCardInputType = (typeof checkboxCardInputTypes)[number];

export enum Layout {
  SHORT = "short",
  TALL = "tall",
  LEFT = "left",
  CENTER = "center",
}

export type InputSize = "xs" | "sm" | "md";

export interface RedoCheckboxCardProps {
  title: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  /**
   * Whether `icon` gets the circular outline. Turn it off for artwork that
   * carries its own shape, such as a brand logo.
   */
  isFramedIcon?: boolean;
  checked: boolean;
  disabled?: boolean;
  onClick?: () => void;
  borderColor?: string;
  inputType?: CheckboxCardInputType;
  inputSize?: InputSize;
  layout?: Layout;
  childrenFullWidth?: boolean;
  dataTestId?: string;
  bgColor?: BgColorValue;
  uiSelector?: string;
}

function requireCssClass(value: string | undefined, name: string): string {
  if (value == null) {
    throw new Error(`Missing CSS module class: ${name}`);
  }
  return value;
}

const OUTER_BORDER = requireCssClass(css.outerBorder, "outerBorder");
const CHECKBOX_CARD = requireCssClass(css.checkboxCard, "checkboxCard");
const CHECKBOX_CARD_SELECTED = requireCssClass(
  css.checkboxCardSelected,
  "checkboxCardSelected",
);
const DISABLED = requireCssClass(css.disabled, "disabled");
const INTERACTIVE = requireCssClass(css.interactive, "interactive");
const CARD_ICON = requireCssClass(css.cardIcon, "cardIcon");
const CARD_ICON_UNFRAMED = requireCssClass(
  css.cardIconUnframed,
  "cardIconUnframed",
);

export const RedoCheckboxCard = memo(function RedoCheckboxCardProps({
  title,
  icon,
  isFramedIcon = true,
  checked,
  disabled,
  onClick,
  inputType,
  inputSize = "md",
  layout = Layout.SHORT,
  childrenFullWidth = false,
  children,
  dataTestId,
  bgColor,
  uiSelector,
}: RedoCheckboxCardProps) {
  const titleText =
    typeof title === "string" ? (
      <Text
        fontSize={inputSizetoTextSize[inputSize]}
        fontWeight={checked ? "medium" : "regular"}
      >
        {title}
      </Text>
    ) : (
      title
    );
  const hasChildren = children != null && children !== false;
  const childrenText =
    typeof children === "string" ? (
      <Text color="secondary" fontSize="sm">
        {children}
      </Text>
    ) : (
      children
    );

  const iconClassName = classnames(CARD_ICON, {
    [CARD_ICON_UNFRAMED]: !isFramedIcon,
  });

  const interactive = !!onClick;
  const clickHandler = useHandler(() => {
    if (!disabled) {
      onClick?.();
    }
  });

  return (
    <Flex
      bgColor={bgColor}
      className={classnames(OUTER_BORDER, {
        [CHECKBOX_CARD_SELECTED]: checked,
        [DISABLED]: disabled,
        [INTERACTIVE]: interactive,
      })}
      data-arbiter="RedoCheckboxCard"
      data-selected={checked || undefined}
      data-selection-card=""
      data-testid={dataTestId}
      data-ui={uiSelector}
      grow={1}
      m="none"
      minW="none"
      onClick={interactive ? clickHandler : undefined}
      p="none"
    >
      <Flex
        className={classnames(CHECKBOX_CARD, {
          [DISABLED]: disabled,
          [INTERACTIVE]: interactive,
        })}
        grow={1}
        minW="none"
      >
        {layout === Layout.LEFT ? (
          <Flex
            flexDirection="column"
            gap={hasChildren ? "xs" : undefined}
            grow={1}
          >
            <Flex
              align={hasChildren ? "flex-start" : "center"}
              justify="space-between"
            >
              <Flex align="center" gap="md" grow={1} justify="flex-start">
                <CardInput
                  checked={checked}
                  disabled={disabled}
                  inputSize={inputSize}
                  inputType={inputType}
                />
                {titleText}
              </Flex>
              {icon && <div className={iconClassName}>{icon}</div>}
            </Flex>
            {hasChildren && (
              <Flex dir="row" flex={1} gap="md" grow={1}>
                <Flex className={inputSizeToSpacerSize[inputSize]} />
                {childrenText}
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex
            align={layout === Layout.CENTER ? "center" : "stretch"}
            flexDirection={layout === Layout.SHORT ? "row" : "column"}
            gap={layout === Layout.TALL ? "3xl" : "lg"}
            grow={1}
            minW="none"
          >
            {(icon || layout === Layout.TALL) && (
              <Flex align="flex-start" justify="space-between">
                {icon && <div className={iconClassName}>{icon}</div>}
                {layout === Layout.TALL && (
                  <CardInput
                    checked={checked}
                    disabled={disabled}
                    inputSize={inputSize}
                    inputType={inputType}
                  />
                )}
              </Flex>
            )}
            <Flex
              align={childrenFullWidth ? "stretch" : "flex-start"}
              dir="column"
              flex={1}
              gap="xs"
              minW="none"
            >
              {titleText}
              {childrenText}
            </Flex>

            {layout === Layout.SHORT && (
              <CardInput
                checked={checked}
                disabled={disabled}
                inputSize={inputSize}
                inputType={inputType}
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

const CardInput = memo(function CardInput({
  checked,
  inputType,
  inputSize,
  disabled,
}: {
  checked: boolean;
  inputType: CheckboxCardInputType | undefined;
  inputSize: InputSize;
  disabled?: boolean;
}) {
  if (!inputType) {
    return null;
  }
  if (inputType === "checkbox") {
    return (
      <span style={{ pointerEvents: "none" }}>
        <RedoCheckbox
          disabled={disabled}
          setValue={() => {}}
          size={inputSizeToCheckboxSize[inputSize]}
          value={checked}
        />
      </span>
    );
  }
  if (inputType === "radio") {
    return (
      <span style={{ pointerEvents: "none" }}>
        <RedoRadioButton
          disabled={disabled}
          onSelect={() => {}}
          selected={checked}
          size={inputSize}
        />
      </span>
    );
  }
  assertNever(inputType);
});

const inputSizeToCheckboxSize: Record<InputSize, RedoCheckboxSize> = {
  xs: "xs",
  md: "md",
  sm: "sm",
};

const inputSizeToSpacerSize: Record<InputSize, string> = {
  xs: requireCssClass(css.spacerXsmall, "spacerXsmall"),
  sm: requireCssClass(css.spacerSmall, "spacerSmall"),
  md: requireCssClass(css.spacerMedium, "spacerMedium"),
};

const inputSizetoTextSize: Record<InputSize, TextSizeValue> = {
  xs: "xs",
  md: "md",
  sm: "sm",
};
